// Libary imports
import { transform as olTransform } from 'ol/proj';
import OlMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import Feature from 'ol/Feature';
import { Coordinate } from 'ol/coordinate';
import { LineString, SimpleGeometry } from 'ol/geom';
import { easeOut } from 'ol/easing';
import * as olExtent from 'ol/extent';
import * as olSphere from 'ol/sphere';
import { Ajax } from '@shared/Ajax';
import { DrawInteractionTool } from '@openlayers/tools/tool-base';
import { Type as OlGeometryType } from 'ol/geom/Geometry';
import View from 'ol/View';
import { Collection } from 'ol';
import BaseLayer from 'ol/layer/Base';
import GroupLayer from 'ol/layer/Group';
import type { EncodedExpression } from 'ol/expr/expression';

// Local imports
import { Notify } from '@shared/ui-native/Notify';
import { CanvasUtils } from '@shared/utils/CanvasUtils';

// Hides features flagged with `hidden: 1`. Must be passed as a WebGL layer OPTION —
// since ol 10 a `filter` key inside the style object is silently ignored.
// `hidden` must be numeric 0/1 because webgl attributes are floats.
export const WEBGL_NOT_HIDDEN_FILTER: EncodedExpression = ['!=', ['get', 'hidden'], 1];

export function latLonToEN(lat: any, lon: any): Coordinate | null {
    const latF = parseFloat(lat);
    const lonF = parseFloat(lon);

    if (isNaN(latF) || latF < -90 || latF > 90) {
        console.warn('Invalid latitude: ', lat, 'lon: ', lon);
        return null;
    }

    if (isNaN(lonF) || lonF < -180 || lonF > 180) {
        console.warn('Invalid longitude: ', lon, 'lat: ', lat);
        return null;
    }

    return olTransform([lonF, latF], 'EPSG:4326', 'EPSG:3857');
}

export function pointsToEN(points: IPoint[]): Coordinate[] | null {
    const coords: Coordinate[] = [];

    for (const p of points) {
        const point = latLonToEN(p.lat, p.lon);

        if (point) {
            coords.push(point);
        } else {
            showBadCoordinatesError();
            return null;
        }
    }

    return coords;
}

const showBadCoordinatesError = _.debounce(() => {
    Notify.error(kuura.getMessage('js_map.invalid_coordinates'));
}, 100);

export function panTo(map: OlMap, e: number, n: number, zoom?: number, animationDuration = 250) {
    if (!$.isNumeric(e) || !$.isNumeric(n)) {
        console.warn(`[ map.panTo ] invalid coordinates, e: ${e}, e: ${n}`);
        return;
    }

    const opts: any = {
        center: [e, n],
        duration: animationDuration,
        easing: easeOut,
    };

    if ($.isNumeric(zoom)) {
        opts.zoom = zoom;
    }

    map.getView().animate(opts);
}

export function fitFeature(map: OlMap, feature: Feature) {
    if (!feature) return;
    fitFeatures(map, [feature]);
}

export function fitFeatures(map: OlMap, features: Feature[] = [], warn = true) {
    features = features.filter(f => !isNullIsland(f));
    const extent = featuresExtent(features);

    if (!isExtentEmpty(extent)) {
        // try fit
        fitExtent(map, extent);
    } else if (features[0]) {
        // try pan
        const [e, n] = (features[0].getGeometry() as SimpleGeometry).getCoordinates();
        panTo(map, e, n);
    } else {
        if (warn) {
            //Check if the key-value pair translation exist else show Eng translation
            const localizedMessage = (msg => (msg === 'js_general.nothing_on_map' ? 'Nothing on the map to zoom to' : msg))(kuura.getMessage('js_general.nothing_on_map'));
            Notify.warn(localizedMessage);
        }
    }
}

export function fitExtent(map: OlMap, extent: olExtent.Extent, animationDuration = 250, padding: [number, number, number, number] = null) {
    if (isExtentEmpty(extent)) return;

    let sidebarWidth = padding?.[1] ?? 80;
    const sidebar = $('.ol-left-padding:not(.fixed)');
    if (sidebar.length) {
        sidebarWidth = parseInt(sidebar.css('left'), 10) < 0 ? 20 : sidebar.width() + 30;
    }
    if (!padding) {
        padding = [50, 80, 50, null];
    }
    if (!padding[3]) {
        padding[3] = sidebarWidth;
    }

    map.getView().fit(extent, {
        padding,
        duration: animationDuration,
        easing: easeOut,
    });
}

export function isExtentEmpty(extent: olExtent.Extent) {
    // we need to check height/width because having only a single point feature on map breaks this

    if (!olExtent || olExtent.isEmpty(extent)) return true;
    if (isNaN(extent[0]) || isNaN(extent[1]) || isNaN(extent[2]) || isNaN(extent[3])) return true;
    if (extent[0] - extent[2] === 0 || extent[1] - extent[3] === 0) return true;
    if (olExtent.getHeight(extent) === 0 || olExtent.getWidth(extent) === 0) return true;

    return false;
}

export function isNullIsland(feature: Feature): boolean {
    if (!feature) return true;

    const geom = feature.getGeometry() as SimpleGeometry;
    if (!geom) return true;

    const [lat, lon] = olTransform(geom.getCoordinates(), 'EPSG:3857', 'EPSG:4326');
    if (Math.abs(lat) < 5 && Math.abs(lon) < 5) return true;

    return false;
}

export function setMapCursor(map: OlMap, cursor: '' | 'crosshair' | 'pointer' | 'not-allowed') {
    (map.getTargetElement() as HTMLElement).style.cursor = cursor;
}

export function featuresExtent(features: Feature[], padZeroExtent = true): olExtent.Extent {
    const extent: olExtent.Extent = olExtent.createEmpty();

    for (const feature of features) {
        if (feature) {
            olExtent.extend(extent, feature.getGeometry().getExtent());
        }
    }
    if (padZeroExtent && features.length > 0 && features.length > 0 && extent[0] === extent[2] && extent[1] === extent[3]) {
        extent[0] -= 50.0; // Create default extent to enable onclick panning in this special case
        extent[2] += 50.0;
    }
    return extent;
}

export function getFeatureIndex(feature: Feature, source: VectorSource): number {
    let index = -1;
    let i = 0;
    source.forEachFeature(f => {
        if (declusterFeature(f) == feature) {
            index = i;
        }
        i++;
    });
    return index;
}

/**
 * Returns first feature from cluster feature
 * When cluster has one feature inside, it is still a cluster and can't be treated like regular feature
 */
export function declusterFeature(clusterFeature: Feature): Feature | null {
    if (!clusterFeature) return null;

    // is cluster
    if (clusterFeature.get('features')) {
        const features: Feature[] = clusterFeature.get('features');
        if (features.length >= 1) {
            // handle cluster with one feature
            return features[0];
        } else {
            return null;
        }
    }

    // not a cluster
    return clusterFeature;
}

export function isCluster(feature) {
    if (!feature) return false;
    if (typeof feature.get !== 'function') return false;
    if (!feature.get('features')) return false;

    return feature.get('features').length > 1;
}

export function mapRotationDegrees(map: OlMap) {
    let deg = (map.getView().getRotation() * (180 / Math.PI)) % 360;
    if (deg < 0) {
        deg = 360 + deg;
    }
    return Math.round(deg);
}

// ol v6+ draws layers on multiple canvases, this merges them to single canvas to be used when taking map screenshots/saved views
export function getCompositeMapCanvas(map: OlMap, includeAnnotations = false): HTMLCanvasElement {
    const newCanvas = document.createElement('canvas');

    const canvasEls = map.getViewport().querySelectorAll('.ol-layer canvas, canvas.ol-layer' + (includeAnnotations ? ', .saved-state-drawings canvas' : '')) as any;
    const size = map.getSize();
    newCanvas.width = size[0] * window.devicePixelRatio;
    newCanvas.height = size[1] * window.devicePixelRatio;
    const mapContext = newCanvas.getContext('2d');
    // we need to prefill because map might be half transparent
    mapContext.fillStyle = 'white';
    mapContext.fillRect(0, 0, newCanvas.width, newCanvas.height);

    canvasEls.forEach((canvas: HTMLCanvasElement) => {
        const parentNode = canvas.parentNode as HTMLDivElement;
        if (canvas.width > 0) {
            if (CanvasUtils.isCanvasTainted(canvas)) {
                return;
            }
            const opacity = parentNode.style.opacity || canvas.style.opacity;
            mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
            let matrix;
            const transform = canvas.style.transform;
            if (transform) {
                matrix = transform
                    .match(/^matrix\(([^\(]*)\)$/)[1]
                    .split(',')
                    .map(Number);
                matrix = matrix.map(p => (p *= window.devicePixelRatio));

                CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
                const backgroundColor = parentNode.style.backgroundColor;
                if (backgroundColor) {
                    mapContext.fillStyle = backgroundColor;
                    mapContext.fillRect(0, 0, canvas.width, canvas.height);
                }
            } else {
                mapContext.resetTransform();
            }

            mapContext.drawImage(canvas, 0, 0);
        }
    });
    mapContext.globalAlpha = 1;
    mapContext.setTransform(1, 0, 0, 1, 0, 0);
    return newCanvas;
}

export function freezeMap(map: OlMap, includeAnnotations = false): HTMLElement | null {
    const mapElement = map.getTargetElement() as HTMLElement;
    let placeholder: HTMLElement | null = null;
    try {
        const snapshot = getCompositeMapCanvas(map, includeAnnotations);
        placeholder = document.createElement('div');
        const style = window.getComputedStyle(mapElement);

        placeholder.style.width = mapElement.style.width || style.width;
        placeholder.style.height = mapElement.style.height || style.height;
        placeholder.style.position = mapElement.style.position || style.position;
        placeholder.style.left = mapElement.style.left || style.left;
        placeholder.style.top = mapElement.style.top || style.top;
        placeholder.style.zIndex = mapElement.style.zIndex || style.zIndex;
        placeholder.className = mapElement.className;

        snapshot.style.width = '100%';
        snapshot.style.height = '100%';
        placeholder.appendChild(snapshot);

        // Keep loading icon visible
        const loadingIcon = mapElement.querySelector('.ol-loading-overlay');
        if (loadingIcon) {
            const clonedIcon = loadingIcon.cloneNode(true) as HTMLElement;
            clonedIcon.style.display = 'block'; // Ensure it's visible
            placeholder.appendChild(clonedIcon);
        }

        // Keep overlays visible (tooltips, labels, etc.)
        const overlayContainer = mapElement.querySelector('.ol-overlaycontainer');
        if (overlayContainer) {
            const clonedOverlayContainer = overlayContainer.cloneNode(true) as HTMLElement;
            clonedOverlayContainer.style.position = 'absolute';
            clonedOverlayContainer.style.top = '0';
            clonedOverlayContainer.style.left = '0';
            clonedOverlayContainer.style.width = '100%';
            clonedOverlayContainer.style.height = '100%';
            clonedOverlayContainer.style.pointerEvents = 'none'; // Prevent interaction with cloned overlays
            placeholder.appendChild(clonedOverlayContainer);
        }

        if (mapElement.parentNode) {
            mapElement.parentNode.insertBefore(placeholder, mapElement);
        }
    } catch (e) {
        console.warn('Failed to create map freeze snapshot', e);
    }
    return placeholder;
}

export function unfreezeMap(placeholder: HTMLElement | null) {
    if (placeholder && placeholder.parentNode) {
        placeholder.parentNode.removeChild(placeholder);
    }
}

export async function getHighResMapCanvas(map: OlMap, includeAnnotations = false, scaleFactor = 2): Promise<HTMLCanvasElement> {
    const mapElement = map.getTargetElement() as HTMLElement;
    const view = map.getView();
    const originalSize = map.getSize();
    const originalZoom = view.getZoom();

    const originalWidth = mapElement.style.width;
    const originalHeight = mapElement.style.height;
    const originalPosition = mapElement.style.position;
    const originalLeft = mapElement.style.left;
    const originalTop = mapElement.style.top;
    const originalVisibility = mapElement.style.visibility;

    try {
        const newWidth = originalSize[0] * scaleFactor;
        const newHeight = originalSize[1] * scaleFactor;

        mapElement.style.position = 'absolute';
        mapElement.style.left = '-99999px';
        mapElement.style.top = '-99999px';
        mapElement.style.visibility = 'hidden';
        mapElement.style.width = newWidth + 'px';
        mapElement.style.height = newHeight + 'px';
        map.updateSize();
        const zoomAdjustment = Math.log2(scaleFactor);
        view.setZoom(originalZoom + zoomAdjustment);

        await new Promise<void>(resolve => {
            map.once('rendercomplete', () => {
                // wait for render just in case
                setTimeout(() => resolve(), 1500);
            });
            map.render();
        });
        const canvas = getCompositeMapCanvas(map, includeAnnotations);
        return canvas;
    } finally {
        view.setZoom(originalZoom);
        mapElement.style.width = originalWidth;
        mapElement.style.height = originalHeight;
        mapElement.style.position = originalPosition;
        mapElement.style.left = originalLeft;
        mapElement.style.top = originalTop;
        mapElement.style.visibility = originalVisibility;
        map.updateSize();
        await new Promise<void>(resolve => {
            map.once('rendercomplete', () => {
                setTimeout(() => resolve(), 50);
            });
            map.render();
        });
    }
}

export function getStationLineDirectionArrow(p1: Coordinate, p2: Coordinate): Coordinate[] {
    const fractionOfStationLineLength = 7;
    const arrowLengthFraction = 4;

    const arrowPoints: Coordinate[] = [];

    const dlat = p1[0] - p2[0];
    const dlon = p1[1] - p2[1];
    const r = Math.sqrt(dlat * dlat + dlon * dlon) / fractionOfStationLineLength;
    let phi = Math.atan2(dlat, dlon) + Math.PI / 2;
    let prev: Coordinate;
    const baseCenter = polar(p1, phi, r);

    const arrow = [1, 4];
    const arrowAngle1 = Math.atan2(arrow[1], -arrow[0]);
    const arrowAngle2 = Math.atan2(-arrow[1], -arrow[0]);
    const arrowLength = Math.sqrt(arrow[0] * arrow[0] + arrow[1] * arrow[1]);

    arrowPoints.push(baseCenter);

    // 1st part of arrow base
    const baseR = r / arrowLengthFraction;
    const basePhi = phi + Math.PI / 2;
    prev = polar(baseCenter, basePhi, baseR);
    arrowPoints.push(prev);

    // 1st side of arrow
    const arrowR = baseR * arrowLength;
    phi = basePhi - arrowAngle1;
    prev = polar(prev, phi, arrowR);
    arrowPoints.push(prev);

    // 2nd side of arrow
    phi = basePhi - arrowAngle2;
    prev = polar(prev, phi, arrowR);
    arrowPoints.push(prev);

    // 2nd part of arrow base
    prev = polar(prev, basePhi, baseR);
    arrowPoints.push(prev);

    arrowPoints.push(baseCenter);

    return arrowPoints;
}

function polar(point: Coordinate, phi: number, r: number): Coordinate {
    const y = point[0] + Math.sin(phi) * r;
    const x = point[1] + Math.cos(phi) * r;

    return [y, x];
}

export function getLineStringLength(geom: LineString, sourceProj = 'EPSG:3857'): number {
    // in meters, has ~100m error for 40km alignment compared to server calculation
    let length = 0;
    const coordinates = geom.getCoordinates();
    const count = coordinates.length;

    for (let i = 0; i < count - 1; i++) {
        const c1 = olTransform(coordinates[i], sourceProj, 'EPSG:4326');
        const c2 = olTransform(coordinates[i + 1], sourceProj, 'EPSG:4326');
        length += olSphere.getDistance(c1, c2);
    }

    return length;
}

export function distanceBetween(p1: Coordinate, p2: Coordinate) {
    return olSphere.getDistance(p1, p2);
}

/**
 * Calculates the length of simple geometries. Complex geometries throw an exception.
 * @param geom
 */
export function getGeomLength(geom: any, sourceProj: any): number {
    const type: OlGeometryType = geom.getType();
    let coordinates;
    let length = 0;

    switch (type) {
        case 'Point':
        case 'MultiPoint':
        case 'LineString':
        case 'LinearRing': {
            coordinates = geom.getCoordinates();
            break;
        }
        case 'Polygon': {
            coordinates = geom.getCoordinates()[0];
            break;
        }
        case 'GeometryCollection':
        case 'MultiLineString':
        case 'MultiPolygon':
        default: {
            throw new Error('Unsupported geometry type: ' + type);
        }
    }

    for (let i = 0, n = coordinates.length - 1; i < n; i++) {
        const c1 = olTransform(coordinates[i], sourceProj, kuura.getProjectProj4Code());
        const c2 = olTransform(coordinates[i + 1], sourceProj, kuura.getProjectProj4Code());

        length += getPointsDistance(c1, c2);
    }

    return length;
}

export function getPointsDistance(c1: number[], c2: number[]): number {
    if (kuura.getProjectProj4Code() === 'EPSG:4326') {
        return olSphere.getDistance(c1, c2) / kuura.getUnit().toMeters;
    }
    return Math.sqrt(Math.pow(c1[0] - c2[0], 2) + Math.pow(c1[1] - c2[1], 2));
}

export async function fitProjectBounds(map: OlMap, projectBounds = null): Promise<void> {
    if (!projectBounds) {
        try {
            projectBounds = await Ajax({ url: 'api/ajax_project_center_point.json' });
        } catch {}
    }

    if (projectBounds?.centerPoint) {
        try {
            const viewExtent = [...latLonToEN(projectBounds.minPoint.lat, projectBounds.minPoint.lon), ...latLonToEN(projectBounds.maxPoint.lat, projectBounds.maxPoint.lon)];
            fitExtent(map, viewExtent, 0);
        } catch (e) {
            console.warn('fitProjectBounds invalid coordinates', projectBounds);
        }
    } else {
        const helsinkiCoord = latLonToEN(60.212808, 24.812256);
        map.getView().setCenter(helsinkiCoord);
        map.getView().setZoom(10);
    }
}

export function getMeasureToolsCoords(distanceTool: DrawInteractionTool, areaTool: DrawInteractionTool) {
    const measurements: { area?: any; distance?: any } = {};
    if (distanceTool.getDrawingsCount()) {
        const coords: Coordinate[][] = distanceTool.drawLayer
            .getSource()
            .getFeatures()
            .map(f => (f.getGeometry() as LineString).getCoordinates());
        measurements.distance = coords.map(coord => {
            return coord.map(point => olTransform(point, 'EPSG:3857', 'EPSG:4326'));
        });
    }
    if (areaTool.getDrawingsCount()) {
        const coords: Coordinate[][] = areaTool.drawLayer
            .getSource()
            .getFeatures()
            .map(f => (f.getGeometry() as LineString).getCoordinates());
        measurements.area = coords.map(coord => {
            return coord.map(outline => {
                return outline.map(point => olTransform(point as any, 'EPSG:3857', 'EPSG:4326'));
            });
        });
    }
    return measurements;
}

export function getSingleMeasurementCoords(feature: Feature, type: 'distance-measurement' | 'area-measurement' | 'volume-measurement'): IMapMeasurementsSavedCoords {
    const measurements: IMapMeasurementsSavedCoords = {};
    const geom = feature.getGeometry() as SimpleGeometry;
    const coords = geom.getCoordinates();

    if (type === 'distance-measurement') {
        const lineCoords = coords as Coordinate[];
        const transformedCoords = lineCoords.map(point => olTransform(point, 'EPSG:3857', 'EPSG:4326'));
        measurements.distance = [transformedCoords];
    } else if (type === 'area-measurement' || type === 'volume-measurement') {
        const polygonCoords = coords as Coordinate[][];
        const transformedCoords = polygonCoords.map(outline => {
            return outline.map(point => olTransform(point, 'EPSG:3857', 'EPSG:4326'));
        });
        if (type === 'area-measurement') {
            measurements.area = [transformedCoords];
        } else {
            measurements.volume = [transformedCoords];
        }
    }
    return measurements;
}

export function getBearing(startLat: number, startLong: number, endLat: number, endLong: number) {
    startLat = startLat * (Math.PI / 180);
    startLong = startLong * (Math.PI / 180);
    endLat = endLat * (Math.PI / 180);
    endLong = endLong * (Math.PI / 180);
    let dLong = endLong - startLong;
    let dPhi = Math.log(Math.tan(endLat / 2.0 + Math.PI / 4.0) / Math.tan(startLat / 2.0 + Math.PI / 4.0));
    if (Math.abs(dLong) > Math.PI) {
        if (dLong > 0.0) dLong = -(2.0 * Math.PI - dLong);
        else dLong = 2.0 * Math.PI + dLong;
    }
    return (Math.atan2(dLong, dPhi) * (180 / Math.PI) + 360.0) % 360.0;
}

export function featureMatchesSource(feature: Feature, source: FeatureSource): boolean {
    const currSource: FeatureSource = feature.get('source');

    if (currSource) {
        if (source.hasOwnProperty('modelId') && source.modelId == currSource.modelId) {
            return true;
        } else if (source.hasOwnProperty('folderId') && source.folderId == currSource.folderId) {
            return true;
        } else if (source.hasOwnProperty('alignmentId') && source.alignmentId == currSource.alignmentId) {
            return true;
        } else if (source.hasOwnProperty('modelName') && source.modelName == currSource.modelName) {
            return true;
        } else if (source.hasOwnProperty('vehicleId') && source.vehicleId == currSource.vehicleId) {
            return true;
        } else if (source.hasOwnProperty('documentId') && source.documentId == currSource.documentId) {
            return true;
        }
    }
    return false;
}

export function getFeaturesBySource(layerSource: VectorSource, source: FeatureSource, invert = false): Feature[] {
    // can't remove from collection while iterating, so we iterate over a copy
    const features: Feature[] = layerSource.getFeatures();
    const matched: Feature[] = [];

    for (let i = 0, ilen = features.length; i < ilen; i++) {
        const curr: Feature = features[i];

        const isMatched = featureMatchesSource(curr, source);

        if ((isMatched && !invert) || (!isMatched && invert)) {
            matched.push(curr);
        }
    }

    return matched;
}

export function removeFeaturesBySource(layerSource: VectorSource, source: FeatureSource, silent = true): Feature[] | null {
    if (layerSource.getFeatures().length === 0) return null; // nothing to remove

    // bulk remove by resetting collection
    // needed because ol.source doesn't implement bulk remove at this time, and removing 20k logpoints one by one takes a minute
    const newCollection: Feature[] = getFeaturesBySource(layerSource, source, true);

    layerSource.clear(silent); // true = Skip dispatching of removeFeature events, must be false in couple of places
    layerSource.addFeatures(newCollection);

    return newCollection;
}
